import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Plus, Trash, PencilSimple, CheckCircle, WarningCircle,
  CloudArrowUp, Copy, ArrowsClockwise,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";

// ─── Status badge helper ─────────────────────────────────────────────────────
function AccountStatusBadge({ status }) {
  const map = {
    connected: ["success", "Connected"],
    error: ["danger", "Error"],
    unchecked: ["default", "Not tested"],
    unknown: ["default", "Unknown"],
  };
  const [v, label] = map[status] || ["default", status];
  return <StatusBadge variant={v}>{label}</StatusBadge>;
}

// ─── Account Card ────────────────────────────────────────────────────────────
function AccountCard({ account, onTest, onEdit, onDelete, onSetDefault, testing }) {
  return (
    <div
      className={`border rounded-lg p-5 bg-card transition-colors ${
        account.is_default ? "border-primary/50 bg-primary/5" : "border-border"
      }`}
      data-testid={`cf-account-card-${account.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-sm" data-testid={`cf-account-name-${account.id}`}>
              {account.name}
            </span>
            {account.is_default && (
              <StatusBadge variant="info">Default</StatusBadge>
            )}
            <AccountStatusBadge status={account.status} />
          </div>
          <div className="mt-2 space-y-1">
            <div className="text-xs text-muted-foreground font-mono">
              Account ID: <span className="text-foreground">{account.account_id}</span>
            </div>
            <div className="text-xs text-muted-foreground font-mono">
              Token: <span className="text-foreground">{account.api_token_masked || "••••••••"}</span>
            </div>
            {account.notes && (
              <div className="text-xs text-muted-foreground">{account.notes}</div>
            )}
            {account.last_verified_at && (
              <div className="text-xs text-muted-foreground">
                Last verified: {new Date(account.last_verified_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onTest(account.id)}
            disabled={testing === account.id}
            data-testid={`test-cf-account-${account.id}`}
            className="text-xs h-7"
          >
            <ArrowsClockwise size={13} className={testing === account.id ? "animate-spin mr-1.5" : "mr-1.5"} />
            Test
          </Button>
          {!account.is_default && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onSetDefault(account.id)}
              data-testid={`set-default-cf-${account.id}`}
              className="text-xs h-7"
            >
              Set Default
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onEdit(account)}
            data-testid={`edit-cf-account-${account.id}`}
            className="text-xs h-7"
          >
            <PencilSimple size={13} className="mr-1.5" />
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(account.id)}
            data-testid={`delete-cf-account-${account.id}`}
            className="text-xs h-7 text-destructive hover:text-destructive"
          >
            <Trash size={13} className="mr-1.5" />
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Add / Edit Modal ────────────────────────────────────────────────────────
function AccountFormModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({
    name: "", account_id: "", api_token: "", notes: "", is_default: false,
  });
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (open) {
      setTestResult(null);
      if (editing) {
        setForm({
          name: editing.name || "",
          account_id: editing.account_id || "",
          api_token: "",
          notes: editing.notes || "",
          is_default: editing.is_default || false,
        });
      } else {
        setForm({ name: "", account_id: "", api_token: "", notes: "", is_default: false });
      }
    }
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Connection name is required");
    if (!form.account_id.trim()) return toast.error("Cloudflare Account ID is required");
    if (!editing && !form.api_token.trim()) return toast.error("API Token is required");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/cloudflare/accounts/${editing.id}`, form);
      } else {
        await api.post("/cloudflare/accounts", form);
      }
      toast.success(editing ? "Account updated" : "Account added");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cf-account-modal">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Cloudflare Account" : "Add Cloudflare Account"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Token scopes guide */}
          {!editing && (
            <div className="bg-muted/50 border border-border rounded-md p-3 text-xs space-y-1">
              <div className="font-semibold text-foreground">Required API Token scopes:</div>
              <div className="text-muted-foreground space-y-0.5">
                <div>• Zone — Zone: Read (All zones)</div>
                <div>• Zone — DNS: Edit (All zones)</div>
                <div>Create token at <span className="font-mono">dash.cloudflare.com → My Profile → API Tokens</span></div>
              </div>
            </div>
          )}

          <div>
            <label className="overline block mb-1">Connection Name *</label>
            <Input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Main Cloudflare Account"
              data-testid="cf-account-name-input"
            />
          </div>
          <div>
            <label className="overline block mb-1">Cloudflare Account ID *</label>
            <Input
              value={form.account_id}
              onChange={(e) => set("account_id", e.target.value)}
              placeholder="32-character hex from CF dashboard sidebar"
              className="font-mono text-xs"
              data-testid="cf-account-id-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Find in: dash.cloudflare.com → any domain → right sidebar → Account ID
            </p>
          </div>
          <div>
            <label className="overline block mb-1">
              API Token {editing ? "(leave blank to keep current)" : "*"}
            </label>
            <Input
              type="password"
              value={form.api_token}
              onChange={(e) => set("api_token", e.target.value)}
              placeholder={editing ? "Enter new token to change" : "cfut_..."}
              data-testid="cf-api-token-input"
            />
          </div>
          <div>
            <label className="overline block mb-1">Notes (optional)</label>
            <Input
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="e.g. Production account"
              data-testid="cf-notes-input"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="cf-default-toggle">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(e) => set("is_default", e.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            <span className="text-sm">Set as default account</span>
          </label>

          {testResult && (
            <div className={`rounded-md p-3 text-xs border ${
              testResult.ok ? "border-green-500/30 bg-green-500/10 text-green-600" : "border-red-500/30 bg-red-500/10 text-red-600"
            }`} data-testid="cf-test-result">
              {testResult.ok ? (
                <div>
                  <div className="font-semibold flex items-center gap-1.5">
                    <CheckCircle size={13} /> Token valid — {testResult.token_status}
                  </div>
                  {testResult.accessible_accounts?.length > 0 && (
                    <div className="mt-1">
                      Accounts: {testResult.accessible_accounts.map((a) => `${a.name} (${a.id})`).join(", ")}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1.5"><WarningCircle size={13} /> {testResult.error}</div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-cf-account-btn">
            {saving ? "Saving…" : (editing ? "Update" : "Add Account")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function CloudflareSettings() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cloudflare/accounts");
      setAccounts(data.accounts || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const { data } = await api.post(`/cloudflare/accounts/${id}/test`);
      toast.success(`Connected — ${data.zones_accessible} zones accessible`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      load();
    } finally {
      setTesting(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/cloudflare/accounts/${id}`);
      toast.success("Account removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await api.put(`/cloudflare/accounts/${id}`, { is_default: true });
      toast.success("Default account updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="Integration"
        title="Cloudflare Accounts"
        description="Connect Cloudflare accounts to enable zone creation, DNS management and automatic nameserver setup."
        actions={
          <Button onClick={() => { setEditing(null); setModalOpen(true); }} data-testid="add-cf-account-btn">
            <Plus size={16} className="mr-2" /> Add Account
          </Button>
        }
      />

      {loading ? (
        <div className="text-muted-foreground text-sm py-16 text-center">Loading accounts…</div>
      ) : accounts.length === 0 ? (
        <div
          className="border border-dashed border-border rounded-lg py-20 text-center"
          data-testid="no-cf-accounts"
        >
          <CloudArrowUp size={40} className="mx-auto mb-4 text-muted-foreground" />
          <div className="font-display font-bold mb-1">No Cloudflare accounts connected</div>
          <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
            Connect a Cloudflare account to start onboarding domains and managing DNS automatically.
          </p>
          <Button onClick={() => { setEditing(null); setModalOpen(true); }} data-testid="add-first-cf-account-btn">
            <Plus size={15} className="mr-2" /> Add your first account
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="cf-accounts-list">
          {accounts.map((a) => (
            <AccountCard
              key={a.id}
              account={a}
              testing={testing}
              onTest={handleTest}
              onEdit={(acc) => { setEditing(acc); setModalOpen(true); }}
              onDelete={(id) => setDeleteConfirm(id)}
              onSetDefault={handleSetDefault}
            />
          ))}
        </div>
      )}

      <AccountFormModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSaved={load}
        editing={editing}
      />

      {/* Delete confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-cf-confirm-modal">
          <DialogHeader>
            <DialogTitle>Remove account?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            This removes the account connection from NameOps. Existing Cloudflare zones are not affected.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-cf-btn">
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
